import React from 'react';
import {
  Card,
  CardContent,
  Grid,
  Box,
  Typography,
  Button,
  Chip,
  Divider,
  Skeleton,
  Paper,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab';
import {
  Edit as EditIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { formatNaira, calculateDiscounts } from '../../../services/fleetSubscriptionService';
import { useCurrentPricing, usePricingHistory } from '../hooks/useFleetSubscriptions';

interface PricingConfigCardProps {
  onUpdatePricing: () => void;
}

const PricingConfigCard: React.FC<PricingConfigCardProps> = ({ onUpdatePricing }) => {
  const { data: currentPricing, isLoading: pricingLoading } = useCurrentPricing();
  const { data: pricingHistory, isLoading: historyLoading } = usePricingHistory();

  if (pricingLoading) {
    return (
      <Card>
        <CardContent>
          <Skeleton variant="rectangular" height={300} />
        </CardContent>
      </Card>
    );
  }

  if (!currentPricing) {
    return (
      <Card>
        <CardContent>
          <Typography variant="body1" color="text.secondary" align="center">
            No pricing configuration found
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const discounts = calculateDiscounts(currentPricing);
  const effectiveDate = new Date(currentPricing.effective_from);

  return (
    <Box>
      {/* Current Pricing */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Current Pricing
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  Effective from: {effectiveDate.toLocaleDateString()}
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={onUpdatePricing}
            >
              Update Pricing
            </Button>
          </Box>

          <Grid container spacing={3}>
            {/* Monthly */}
            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  border: '2px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  textAlign: 'center',
                  transition: 'all 0.3s',
                  '&:hover': {
                    borderColor: 'primary.main',
                    transform: 'translateY(-4px)',
                    boxShadow: 3,
                  },
                }}
              >
                <Typography variant="overline" color="text.secondary" gutterBottom>
                  Monthly
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main', my: 2 }}>
                  {formatNaira(currentPricing.monthly_rate)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Standard rate
                </Typography>
              </Paper>
            </Grid>

            {/* Quarterly */}
            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  border: '2px solid',
                  borderColor: 'success.main',
                  borderRadius: 2,
                  textAlign: 'center',
                  position: 'relative',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 3,
                  },
                }}
              >
                <Chip
                  label={`${discounts.quarterly_discount_percent}% off`}
                  color="success"
                  size="small"
                  sx={{ position: 'absolute', top: 12, right: 12 }}
                />
                <Typography variant="overline" color="text.secondary" gutterBottom>
                  Quarterly (3 Months)
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 700, color: 'success.main', my: 2 }}>
                  {formatNaira(currentPricing.quarterly_rate)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Save {formatNaira(currentPricing.monthly_rate * 3 - currentPricing.quarterly_rate)}
                </Typography>
              </Paper>
            </Grid>

            {/* Yearly */}
            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  border: '2px solid',
                  borderColor: '#9333ea',
                  borderRadius: 2,
                  textAlign: 'center',
                  position: 'relative',
                  background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.05), rgba(59, 130, 246, 0.05))',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 3,
                  },
                }}
              >
                <Chip
                  label={`${discounts.yearly_discount_percent}% off`}
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    background: 'linear-gradient(45deg, #9333ea, #c084fc)',
                    color: 'white',
                  }}
                  size="small"
                />
                <Typography variant="overline" color="text.secondary" gutterBottom>
                  Yearly (12 Months)
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 700, color: '#9333ea', my: 2 }}>
                  {formatNaira(currentPricing.yearly_rate)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Save {formatNaira(currentPricing.monthly_rate * 12 - currentPricing.yearly_rate)}
                </Typography>
                <Chip
                  label="Best Value"
                  color="primary"
                  size="small"
                  sx={{ mt: 1 }}
                />
              </Paper>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Metadata */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  Modified by: Admin #{currentPricing.created_by}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  Last updated: {new Date(currentPricing.updated_at).toLocaleString()}
                </Typography>
              </Box>
            </Grid>
            {currentPricing.notes && (
              <Grid item xs={12}>
                <Box sx={{ p: 2, backgroundColor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    Notes:
                  </Typography>
                  <Typography variant="body2">{currentPricing.notes}</Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Pricing History */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            Pricing History
          </Typography>

          {historyLoading ? (
            <Box>
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} variant="rectangular" height={80} sx={{ mb: 2 }} />
              ))}
            </Box>
          ) : pricingHistory && pricingHistory.length > 0 ? (
            <Timeline position="right">
              {pricingHistory.slice(0, 5).map((pricing, index) => {
                const isActive = pricing.is_active;
                const startDate = new Date(pricing.effective_from);
                const endDate = pricing.effective_to ? new Date(pricing.effective_to) : null;
                const historicalDiscounts = calculateDiscounts(pricing);

                return (
                  <TimelineItem key={pricing.id}>
                    <TimelineOppositeContent sx={{ flex: 0.3 }}>
                      <Typography variant="caption" color="text.secondary">
                        {startDate.toLocaleDateString()}
                      </Typography>
                      {endDate && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          to {endDate.toLocaleDateString()}
                        </Typography>
                      )}
                    </TimelineOppositeContent>
                    <TimelineSeparator>
                      <TimelineDot color={isActive ? 'success' : 'grey'}>
                        {isActive ? <CheckCircleIcon /> : <TrendingUpIcon />}
                      </TimelineDot>
                      {index < pricingHistory.length - 1 && <TimelineConnector />}
                    </TimelineSeparator>
                    <TimelineContent>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          backgroundColor: isActive ? 'action.selected' : 'action.hover',
                          border: isActive ? '1px solid' : 'none',
                          borderColor: 'primary.main',
                        }}
                      >
                        {isActive && (
                          <Chip
                            label="Current"
                            color="success"
                            size="small"
                            sx={{ mb: 1 }}
                          />
                        )}
                        <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                          <Typography variant="body2">
                            Monthly: <strong>{formatNaira(pricing.monthly_rate)}</strong>
                          </Typography>
                          <Typography variant="body2">
                            Quarterly: <strong>{formatNaira(pricing.quarterly_rate)}</strong>
                            <Chip
                              label={`${historicalDiscounts.quarterly_discount_percent}% off`}
                              size="small"
                              sx={{ ml: 0.5, height: 18 }}
                            />
                          </Typography>
                          <Typography variant="body2">
                            Yearly: <strong>{formatNaira(pricing.yearly_rate)}</strong>
                            <Chip
                              label={`${historicalDiscounts.yearly_discount_percent}% off`}
                              size="small"
                              sx={{ ml: 0.5, height: 18 }}
                            />
                          </Typography>
                        </Box>
                        {pricing.notes && (
                          <Typography variant="caption" color="text.secondary">
                            {pricing.notes}
                          </Typography>
                        )}
                      </Paper>
                    </TimelineContent>
                  </TimelineItem>
                );
              })}
            </Timeline>
          ) : (
            <Typography variant="body2" color="text.secondary" align="center">
              No pricing history available
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default PricingConfigCard;
